/*
 * SourcesPanel — the trip funnel.
 *
 * One card per broker. Live sources first (Kaizen, OVA, Manual, Webhook),
 * then Tier 1 nationals scaffolded (ModivCare, MTM, MAS, ...),
 * then Tier 2 regionals (Verida, LCP, CTS, MDHHS ...).
 */
import React, { useEffect, useState } from "react";
import { apiClient } from "../../lib/api";

const C = { forest: "#2B4C3B", ink: "#0F172A", dim: "#475569", line: "#E2E0D8", amber: "#B45309" };

const STATUS_STYLE = {
  active:     { bg: "#F0FDF4", border: "#BBF7D0", color: "#166534", label: "● Active" },
  scaffolded: { bg: "#F5F3EA", border: "#E2E0D8", color: "#78716C", label: "○ Contract required" },
  paused:     { bg: "#FEF3C7", border: "#FCD34D", color: "#92400E", label: "‖ Paused" },
  error:      { bg: "#FEE2E2", border: "#FCA5A5", color: "#B91C1C", label: "! Error" },
};

export default function SourcesPanel({ onNavigate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await apiClient.get("/admin/sources"); setItems(data.items || []); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-4" data-testid="sources-panel">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: C.ink }}>Sources</h1>
        <div className="text-sm" style={{ color: C.dim }}>
          Every trip in MedexOVA comes from one Source. Bulk CSV import + Kaizen + OVA + webhook are already live. National + regional brokers below are scaffolded — enable them once the provider contract is signed.
        </div>
      </div>
      {loading ? (
        <div className="p-8 text-sm text-center" style={{ color: C.dim }}>Loading sources…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(s => {
            const style = STATUS_STYLE[s.status] || STATUS_STYLE.scaffolded;
            return (
              <div key={s.name} data-testid={`source-card-${s.name}`}
                className="rounded-2xl border p-4 flex flex-col gap-2"
                style={{ borderColor: C.line, background: "#fff" }}>
                <div className="flex items-start justify-between">
                  <div className="text-2xl">{s.icon}</div>
                  <span className="text-[10px] px-2 py-1 rounded-full font-bold"
                    style={{ background: style.bg, border: `1px solid ${style.border}`, color: style.color }}>
                    {style.label}
                  </span>
                </div>
                <div>
                  <div className="font-bold text-sm" style={{ color: C.ink }}>{s.label}</div>
                  <div className="text-xs mt-1" style={{ color: C.dim }}>{s.coverage}</div>
                </div>
                <div className="text-[11px] font-mono" style={{ color: C.dim }}>
                  Tier {s.tier} · {s.supports_realtime ? "Realtime" : "Poll"}
                </div>
                {s.name === "kaizen" && (
                  <button onClick={() => onNavigate?.("kaizen")}
                    data-testid={`open-source-${s.name}`}
                    className="mt-1 px-3 py-1.5 rounded-md text-xs font-bold text-white text-center"
                    style={{ background: C.forest }}>Open Kaizen inbox →</button>
                )}
                {s.name === "ova" && (
                  <button onClick={() => onNavigate?.("ova")}
                    data-testid={`open-source-${s.name}`}
                    className="mt-1 px-3 py-1.5 rounded-md text-xs font-bold text-white text-center"
                    style={{ background: C.forest }}>Open OVA dispatch →</button>
                )}
                {s.name === "manual" && (
                  <button onClick={() => onNavigate?.("import")}
                    data-testid={`open-source-${s.name}`}
                    className="mt-1 px-3 py-1.5 rounded-md text-xs font-bold text-white text-center"
                    style={{ background: C.forest }}>Open CSV import →</button>
                )}
                {s.status === "scaffolded" && !["kaizen","ova","manual","webhook"].includes(s.name) && (
                  <div className="mt-1 text-[11px] italic" style={{ color: C.amber }}>Contract required — see BROKER_APIS.md</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
